import { useTranslation } from 'react-i18next';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { FormSidePanel } from '@/shared/ui/layout/FormSidePanel';
import type { ConfigureContentElementSheetProps } from './ConfigureContentElementSheet.types';
import { ConfigureContentElementFields } from './ConfigureContentElementFields';
import { ConfigureContentElementFooterActions } from './ConfigureContentElementFooterActions';
import { useConfigureContentElementForm } from './useConfigureContentElementForm';

/**
 * Content-element configuration on a right-side sheet: naming, content/image
 * editing, custom attributes and deletion. The behaviour behind all of it lives
 * in `useConfigureContentElementForm`.
 */
export function ConfigureContentElementSheet(props: ConfigureContentElementSheetProps) {
  const { t } = useTranslation('admin');
  const { target, onClose, useKnowledgeBaseImagesHook } = props;

  const form = useConfigureContentElementForm(props);

  if (!target) return null;

  return (
    <>
      <FormSidePanel
        open
        onOpenChange={(next) => !next && !form.saving && onClose()}
        title={t('templatesConfig.configureElement.title')}
        className="w-[560px] gap-sm"
        footer={
          <ConfigureContentElementFooterActions
            saving={form.saving}
            canSave={form.canSave}
            deleteBlockedReason={form.deleteBlockedReason}
            onClose={onClose}
            onSave={() => void form.handleSave()}
            onRequestDelete={() => form.setConfirmDeleteOpen(true)}
          />
        }
      >
        <div className="flex flex-col gap-md">
          <ConfigureContentElementFields
            target={target}
            isImage={form.isImage}
            canEditContent={form.canEditContent}
            sectionName={form.sectionName}
            setSectionName={form.setSectionName}
            slugError={form.slugError}
            content={form.content}
            setContent={form.setContent}
            setImageValue={form.setImageValue}
            imageDescription={form.imageDescription}
            setImageDescription={form.setImageDescription}
            attributes={form.attributes}
            setAttributes={form.setAttributes}
            useKnowledgeBaseImagesHook={useKnowledgeBaseImagesHook}
          />
        </div>
      </FormSidePanel>

      <DeleteConfirmationModal
        open={form.confirmDeleteOpen}
        onCancel={() => form.setConfirmDeleteOpen(false)}
        onConfirmDelete={form.handleDelete}
        customModalTitle={t('templatesConfig.configureElement.deleteElementTitle', {
          tagName: target.tagName,
        })}
        warningMessage={
          form.sectionsAtRisk && form.sectionsAtRisk.length > 0
            ? t('templatesConfig.configureElement.deleteElementWarningWithSections', {
                count: form.sectionsAtRisk.length,
                sections: form.sectionsAtRisk.join(', '),
              })
            : t('templatesConfig.configureElement.deleteElementWarning')
        }
        data-testid="configure-element-delete-confirmation"
      />
    </>
  );
}

ConfigureContentElementSheet.displayName = 'ConfigureContentElementSheet';
